import React from "react";
import Image from "next/image";
import { Link } from "@/i18n/routing";
import { Card, CardContent } from "@/components/ui/card";
import { FadeIn, Stagger } from "@/components/ui/scroll-animated";
import { useTranslations } from 'next-intl';
import { useDigitalSolutionsData } from '@/contexts/HomeDataContext';

interface DigitalSolutionsSectionProps {
    anchorId?: string;
}

export const DigitalSolutionsSection: React.FC<DigitalSolutionsSectionProps> = ({ anchorId }) => {
  const t = useTranslations('DigitalSolutions');
  const digitalSolutions = useDigitalSolutionsData();

  return (
    <section id={anchorId} className="w-full relative py-16 md:py-20 lg:py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <FadeIn delay={150} className="mb-3">
            <div className="bg-brand-gradient-text font-h1-up font-[number:var(--h1-up-font-weight)] text-[length:var(--h1-up-font-size)] tracking-[var(--h1-up-letter-spacing)] leading-[var(--h1-up-line-height)] [font-style:var(--h1-up-font-style)]">
              {t('tagline')}
            </div>
          </FadeIn>
          <FadeIn delay={250}>
            <h2 className="font-heading-h1 font-[number:var(--heading-h1-font-weight)] text-x-900 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[length:var(--heading-h1-font-size)] tracking-[var(--heading-h1-letter-spacing)] leading-tight md:leading-tight lg:leading-[var(--heading-h1-line-height)] [font-style:var(--heading-h1-font-style)] max-w-[700px] mx-auto">
              {t('heading')}
            </h2>
          </FadeIn>
        </div>

        {/* List */}
        <div className="flex flex-col gap-6">
          {digitalSolutions.serviceCards.map((service, index) => {
            const isWebDevelopment = service.id === "web-development-notworking";
            const cardContent = (
              <Card
                className={`relative w-full h-auto md:h-[186px] bg-white rounded-[10px] overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 ease-out group-hover:-translate-y-1 group-hover:shadow-xl has-gradient-left before:content-[''] before:absolute before:inset-y-0 before:left-0 before:w-0 group-hover:before:w-[8px] md:group-hover:before:w-[10px] before:rounded-l-[10px] before:opacity-0 group-hover:before:opacity-100 before:transition-all before:duration-300 ${isWebDevelopment ? 'cursor-pointer' : ''}`}
              >
                <CardContent className="p-0 h-full flex flex-col md:flex-row relative">
                  {/* Text */}
                  <div className="flex-1 flex flex-col justify-center gap-3 md:pl-10 pl-6 py-6">
                    <h3 className="font-heading-h2 font-[number:var(--heading-h2-font-weight)] text-x-900 text-xl md:text-2xl tracking-[var(--heading-h2-letter-spacing)] leading-[var(--heading-h2-line-height)] [font-style:var(--heading-h2-font-style)]">
                      {t(`services.${service.id}.title`)}
                    </h3>
                    <p className="font-body-m font-[number:var(--body-m-font-weight)] text-x-800 text-sm md:text-base tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] max-w-[520px]">
                      {t(`services.${service.id}.description`)}
                    </p>
                  </div>

                  {/* CTA */}
                  {/* <div className="flex items-center justify-center md:px-8 px-6 pb-6 md:pb-0">
                    {isWebDevelopment ? (
                      <Link href="/solutions/web-development">
                        <Button
                          variant="outline"
                          className="h-[54px] px-6 rounded-[8px] gap-2 transition-all duration-300 ease-out relative overflow-hidden btn-shine border-gray-300 text-x-900 hover:bg-gray-50 group-hover:bg-brand-gradient group-hover:text-white group-hover:border-transparent group-hover:shadow-[0_8px_24px_rgba(63,116,239,0.35)]"
                        >
                          <span className="font-button font-[number:var(--button-font-weight)] text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)]">
                            {t('buttonText')}
                          </span>
                          <ArrowRightIcon className="w-4 h-4" />
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        variant="outline"
                        className="h-[54px] px-6 rounded-[8px] gap-2 transition-all duration-300 ease-out relative overflow-hidden btn-shine border-gray-300 text-x-900 hover:bg-gray-50 group-hover:bg-brand-gradient group-hover:text-white group-hover:border-transparent group-hover:shadow-[0_8px_24px_rgba(63,116,239,0.35)]"
                      >
                        <span className="font-button font-[number:var(--button-font-weight)] text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)]">
                          {t('buttonText')}
                        </span>
                        <ArrowRightIcon className="w-4 h-4" />
                      </Button>
                    )}
                  </div> */}

                  {/* Image */}
                  <div className="md:w-[300px] w-full md:h-auto h-[180px] relative shine-on-hover">
                    <Image
                      src={service.image}
                      alt={`${t(`services.${service.id}.title`)} service`}
                      fill
                      className="object-cover md:rounded-[0px_10px_10px_0px] rounded-b-[10px]"
                      sizes="(min-width: 768px) 300px, 100vw"
                    />
                  </div>
                </CardContent>
              </Card>
            );

            return (
              <Stagger key={service.id} index={index} delay={200} staggerDelay={150} className="group">
                {isWebDevelopment ? (
                  <Link href="/solutions/web-development" className="block">
                    {cardContent}
                  </Link>
                ) : (
                  cardContent
                )}
              </Stagger>
            );
          })}
        </div>
      </div>
    </section>
  );
};


